/**
 * Floating action button — sutil, lleva al flow VIP (`/invitacion-premium`).
 *
 * En Argentina solo se muestra si el admin habilitó `community` para la geo
 * del visitante (misma regla que el gate de la landing). Exterior: siempre.
 */

import { Link } from "react-router-dom";
import { Crown } from "lucide-react";
import { isPremiumInviteLandingAllowed } from "@/lib/premiumInviteAccess";
import { PREMIUM_INVITE_LANDING_PATH } from "@/lib/premiumVipCheckoutDestination";
import { getServerTemplateConfig } from "@/lib/serverTemplateDetection";

interface MembershipFloatingCtaProps {
  /** Destino del CTA. Default `/invitacion-premium`. */
  to?: string;
  /** Override del label. Default "Membresía". */
  label?: string;
  /** Posición. Default "top-right". */
  position?: "top-right" | "bottom-center";
}

export function MembershipFloatingCta({
  to = PREMIUM_INVITE_LANDING_PATH,
  label = "Membresía",
  position = "top-right",
}: MembershipFloatingCtaProps) {
  const edge = getServerTemplateConfig();
  const allowed = isPremiumInviteLandingAllowed({
    edgeCountry: edge?.country,
    showCommunity: edge?.showCommunity,
  });
  if (!allowed) return null;

  const positionClasses =
    position === "top-right"
      ? "top-3 right-3"
      : "left-1/2 -translate-x-1/2 bottom-4";

  return (
    <Link
      to={to}
      className={`absolute z-[60] ${positionClasses} inline-flex items-center gap-1.5 rounded-full bg-black/65 backdrop-blur-md px-3.5 py-2 text-[12px] font-medium text-white/95 border border-white/15 shadow-md transition-colors duration-150 hover:bg-black/80 hover:border-white/25 active:bg-black/90 no-underline`}
      style={{
        // Respeta notch en iOS cuando está top
        ...(position === "top-right"
          ? { top: "max(0.75rem, env(safe-area-inset-top, 0.75rem))" }
          : {
              bottom: "max(1rem, env(safe-area-inset-bottom, 1rem))",
            }),
        touchAction: "manipulation",
        WebkitTapHighlightColor: "transparent",
      }}
      aria-label={label}
    >
      <Crown className="h-3.5 w-3.5 text-amber-300" aria-hidden />
      <span className="tracking-tight">{label}</span>
    </Link>
  );
}
